import React, { useState } from 'react'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'
import { accents } from 'sutra/tokens/colors'

import { type BreakdownSelection } from 'features/charts-v2/components/breakdown-selection-utils'
import { BreakdownSelectionPopover } from 'features/charts-v2/components/BreakdownSelectionPopover'
import {
  type BreakdownOption,
  type CustomBreakdown,
} from 'features/charts-v2/types'

// Groups the optional sync toggle with the add button on the header's right.
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

interface BreakdownSelectHeaderProps {
  onSelect: (breakdown: BreakdownSelection) => void
  selectedBreakdown?: BreakdownSelection
  // Constraint props
  isDisabled?: boolean
  disabledTooltip?: string
  invalidBreakdowns?: BreakdownOption[]
  getInvalidReason?: (breakdown: BreakdownOption) => string | null
  // Custom breakdowns from the project
  customBreakdowns?: CustomBreakdown[]
  // All selected breakdowns (to prevent duplicates)
  allSelectedBreakdowns?: BreakdownSelection[]
  // Whether to show the model hierarchy prefix (e.g. "Post > Theme")
  showBreakdownHierarchy: boolean
  // Optional cross-panel sync toggle, rendered left of the add button.
  syncToggle?: React.ReactNode
}

export const BreakdownSelectHeader: React.FC<BreakdownSelectHeaderProps> = ({
  onSelect,
  selectedBreakdown,
  isDisabled = false,
  disabledTooltip,
  invalidBreakdowns = [],
  getInvalidReason,
  customBreakdowns = [],
  allSelectedBreakdowns = [],
  showBreakdownHierarchy,
  syncToggle,
}) => {
  const [showPopover, setShowPopover] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const handleSelect = (breakdown: BreakdownSelection): void => {
    onSelect(breakdown)
    setShowPopover(false)
  }

  const handleClosePopover = (): void => {
    setShowPopover(false)
    setTooltipOpen(false)
  }

  const handleClick = (): void => {
    if (!isDisabled) {
      setShowPopover(!showPopover)
    }
  }

  const tooltipTitle = (
    <div>
      <div>Choose how to group and breakdown your chart data.</div>
      {isDisabled && disabledTooltip && (
        <div style={{ marginTop: '4px', color: 'gray' }}>{disabledTooltip}</div>
      )}
    </div>
  )

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: '8px',
        position: 'relative',
      }}
    >
      <h2 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>
        Breakdown
      </h2>

      <HeaderActions>
        {syncToggle}
        <Tooltip open={tooltipOpen} content={tooltipTitle} side="right">
          <button
            type="button"
            onClick={() => {
              setTooltipOpen(false)
              handleClick()
            }}
            onMouseEnter={() => {
              setIsHovered(true)
              if (!showPopover) setTooltipOpen(true)
            }}
            onMouseLeave={() => {
              setIsHovered(false)
              setTooltipOpen(false)
            }}
            style={{
              width: 30,
              height: 30,
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: isDisabled
                ? '#f5f5f5'
                : isHovered || showPopover
                  ? accents.reflectionBlue[100]
                  : '#ffffff',
              color: isDisabled ? '#9ca3af' : '#6b7280',
              fontSize: '18px',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              padding: 0,
            }}
          >
            +
          </button>
        </Tooltip>
      </HeaderActions>

      {/* Breakdown Selection Popover */}
      {showPopover && !isDisabled && (
        <BreakdownSelectionPopover
          onBreakdownSelect={handleSelect}
          onClose={handleClosePopover}
          selectedBreakdown={selectedBreakdown}
          invalidBreakdowns={invalidBreakdowns}
          customBreakdowns={customBreakdowns}
          getInvalidReason={getInvalidReason}
          allSelectedBreakdowns={allSelectedBreakdowns}
          showBreakdownHierarchy={showBreakdownHierarchy}
        />
      )}
    </div>
  )
}
